import React from 'react';
import { motion } from 'framer-motion';
import { Search, FlaskConical, Terminal, Rocket } from 'lucide-react';
import Card from '../ui/Card';

const lifecycleSteps = [
  {
    step: '01',
    icon: <Search className="w-5 h-5 text-brand-600 dark:text-brand-400" />,
    title: 'Research',
    subheading: 'Problem Analysis & Algorithm Design',
    description: 'We investigate real-world bottlenecks in healthcare, security, and mobility, exploring cutting-edge algorithmic papers to establish solid architectural foundations.',
    deliverables: ['Mathematical & domain modeling', 'Algorithm feasibility analysis', 'Security threat modeling']
  },
  {
    step: '02',
    icon: <FlaskConical className="w-5 h-5 text-indigoBrand-600 dark:text-indigoBrand-400" />,
    title: 'Prototype',
    subheading: 'Sandbox Testing & Validation',
    description: 'We build rigorous sandbox prototypes to benchmark inference latency, classification accuracy, and user interaction mechanics under realistic constraints.',
    deliverables: ['Functional proof-of-concepts', 'Benchmark latency testing', 'User workflow validation']
  },
  {
    step: '03',
    icon: <Terminal className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />,
    title: 'Engineering',
    subheading: 'Production-Grade Hardening',
    description: 'Prototypes are translated into production-grade systems engineered with clean architectures, zero-trust security auditing, and high concurrency.',
    deliverables: ['Microservice architecture', 'Automated test coverage', 'Zero-trust security compliance']
  },
  {
    step: '04',
    icon: <Rocket className="w-5 h-5 text-amber-600 dark:text-amber-400" />,
    title: 'Deployment',
    subheading: 'Continuous Telemetry & Operation',
    description: 'Platforms are rolled out to containerized cloud clusters with active telemetry, monitoring uptime, latency anomalies, and automated failover.',
    deliverables: ['Containerized orchestration', '24/7 telemetry & alerting', 'Iterative product updates']
  }
];

const Innovation = () => {
  return (
    <section id="innovation" className="py-24 relative bg-white dark:bg-slate-950 border-t border-slate-200/80 dark:border-slate-800">
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4"
          >
            Engineering Methodology
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4 tracking-tight"
          >
            From Idea to Impact
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed"
          >
            How Mira Future Tech Vision builds reliable, production-ready technology through a disciplined four-stage engineering lifecycle.
          </motion.p>
        </div>

        {/* 4 Process Cards with visual horizontal connecting track */}
        <div className="relative">
          {/* Subtle horizontal connecting line on large screens */}
          <div className="hidden lg:block absolute top-1/2 left-8 right-8 h-0.5 bg-slate-200 dark:bg-slate-800 -translate-y-12 z-0" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {lifecycleSteps.map((phase, idx) => (
              <motion.div
                key={phase.step}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-7 shadow-subtle hover:shadow-card transition-all duration-300 flex flex-col justify-between">
                  <div>
                    {/* Header: Number and Icon */}
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-2xl font-bold text-slate-300 dark:text-slate-700">
                        {phase.step}
                      </span>
                      <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 flex items-center justify-center">
                        {phase.icon}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold font-display text-slate-900 dark:text-white mb-1">
                      {phase.title}
                    </h3>
                    <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 mb-3">
                      {phase.subheading}
                    </p>

                    <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-6">
                      {phase.description}
                    </p>
                  </div>

                  {/* Deliverables list */}
                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Key Milestones</p>
                    {phase.deliverables.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Guarantee Note */}
        <div className="mt-12 text-center">
          <p className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-50 dark:bg-slate-900 px-4 py-2 rounded-full border border-slate-200 dark:border-slate-800">
            <span>Every software artifact undergoes automated linting, security scans, and peer review prior to release.</span>
          </p>
        </div>

      </div>
    </section>
  );
};

export default Innovation;
